'use client';

import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const benefits = [
  {
    icon: 'StarIcon',
    title: '3 mois accès premium offerts',
    desc: 'Accès complet à toutes les fonctionnalités GiteZen sur 1 logement actif, sans frais ni engagement. Valeur réelle : 147€.',
    tag: 'Valeur 147€',
    span: 'col-span-1',
    size: 'large',
    accent: 'moss',
  },
  {
    icon: 'UserGroupIcon',
    title: 'Session onboarding 1h personnalisée',
    desc: 'Un expert GiteZen vous guide pas à pas dans la configuration de votre compte selon votre structure spécifique.',
    tag: 'Valeur 120€',
    span: 'col-span-1',
    size: 'normal',
    accent: 'gold',
  },
  {
    icon: 'DocumentTextIcon',
    title: 'Étude de cas professionnelle',
    desc: 'Nous rédigeons et publions une étude de cas détaillée sur votre conciergerie : visibilité SEO, preuve sociale, référence métier.',
    tag: 'Valeur 180€',
    span: 'col-span-1 md:col-span-2 lg:col-span-1',
    size: 'normal',
    accent: 'moss',
  },
  {
    icon: 'MegaphoneIcon',
    title: 'Mise en avant blog GiteZen',
    desc: 'Votre conciergerie présentée dans nos articles, newsletters et contenus. Trafic qualifié vers votre site, backlinks de qualité.',
    tag: 'Valeur 80€',
    span: 'col-span-1',
    size: 'normal',
    accent: 'gold',
  },
  {
    icon: 'ShieldCheckIcon',
    title: 'Badge Conciergerie Partenaire',
    desc: 'Un badge officiel à afficher sur votre site et vos réseaux. Signal de qualité et de structuration professionnelle pour vos propriétaires.',
    tag: 'Crédibilité',
    span: 'col-span-1',
    size: 'normal',
    accent: 'moss',
  },
  {
    icon: 'BoltIcon',
    title: 'Accès prioritaire aux nouvelles fonctionnalités',
    desc: 'Vous testez en avant-première les nouvelles features, influencez la roadmap et bénéficiez des mises à jour avant tout le monde.',
    tag: 'Exclusif',
    span: 'col-span-1 md:col-span-2 lg:col-span-1',
    size: 'normal',
    accent: 'gold',
  },
];

export default function BenefitsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-el').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="avantages" className="py-28 px-6 border-t border-white/[0.05]" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="reveal-el">
            <p className="text-moss-light font-bold uppercase tracking-[0.25em] text-xs mb-4">Ce que vous recevez</p>
            <h2 className="font-fraunces text-5xl md:text-6xl font-light italic leading-tight text-cream">
              Votre programme,<br />
              <span className="text-moss-light">votre avantage.</span>
            </h2>
          </div>
          <div className="glass-card rounded-2xl px-6 py-4 text-right reveal-el">
            <p className="text-cream/40 text-xs uppercase tracking-widest mb-1">Valeur totale estimée</p>
            <p className="font-fraunces text-4xl italic text-gold">500 € </p>
            <p className="text-cream/40 text-xs mt-1">offerts aux partenaires sélectionnés</p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((benefit, i) => (
            <div
              key={i}
              className={`spotlight-card glass-card rounded-2xl p-7 flex flex-col gap-5 group reveal-el ${benefit.span}`}
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="flex items-start justify-between">
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    benefit.accent === 'moss' ?'bg-moss/15 border border-moss/30' :'bg-gold/10 border border-gold/20'
                  } group-hover:scale-110 transition-transform duration-300`}
                >
                  <Icon
                    name={benefit.icon as any}
                    size={22}
                    className={benefit.accent === 'moss' ? 'text-moss-light' : 'text-gold'}
                  />
                </div>
                <span
                  className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${
                    benefit.accent === 'moss' ?'bg-moss/10 text-moss-light' :'bg-gold/10 text-gold'
                  }`}
                >
                  {benefit.tag}
                </span>
              </div>
              <div>
                <h3 className="text-cream font-semibold text-base mb-2">{benefit.title}</h3>
                <p className="text-cream/50 text-sm leading-relaxed">{benefit.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Ticker */}
        <div className="mt-12 overflow-hidden border-t border-b border-white/[0.05] py-5">
          <div className="flex w-max animate-ticker">
            {[...Array(2)].map((_, j) => (
              <div key={j} className="flex items-center gap-12 px-6">
                {['Premium offert', 'Onboarding personnalisé', 'Étude de cas', 'Badge partenaire', 'Accès prioritaire', 'Mise en avant blog', 'Sans engagement', '500€+ de valeur'].map((item, i) => (
                  <span key={i} className="flex items-center gap-3 text-cream/30 text-xs font-semibold tracking-[0.2em] uppercase whitespace-nowrap">
                    <span className="w-1 h-1 rounded-full bg-moss-light" />
                    {item}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}